import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import SubSessionTypeForm from "./SubSessionTypeForm";
import { fetchSubSessionMaster, updateSubSessionMaster } from "../../store/slices/subSessionSlice";
import { getApiErrorMessage } from "../../utils/apiError";

const EditSubSessionType = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { currentSubSessionMaster, isLoading, error } = useSelector(
        (state) => state.subSessions
    );

    const [formData, setFormData] = useState({
        sessionCategoryMasterId: "",
        sessionMasterId: "",
        department: "",
        name: "",
        description: "",
        status: "active",
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (id) {
            dispatch(fetchSubSessionMaster(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (currentSubSessionMaster && Object.keys(currentSubSessionMaster).length > 0) {
            setFormData({
                sessionCategoryMasterId: currentSubSessionMaster.session_category_master_id || "",
                sessionMasterId: currentSubSessionMaster.session_master_id || "",
                department: currentSubSessionMaster.department || "",
                name: currentSubSessionMaster.name || "",
                description: currentSubSessionMaster.description || "",
                status: currentSubSessionMaster.status || "active",
            });
        }
    }, [currentSubSessionMaster]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.sessionCategoryMasterId) newErrors.sessionCategoryMasterId = "Session Category is required";
        if (!formData.sessionMasterId) newErrors.sessionMasterId = "Session Type is required";
        if (!formData.department) newErrors.department = "Department is required";
        if (!formData.name.trim()) newErrors.name = "Name is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            Swal.fire({
                title: "Missing Fields",
                text: "Please fill all required fields.",
                icon: "warning",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            const payload = {
                session_category_master_id: formData.sessionCategoryMasterId,
                session_master_id: formData.sessionMasterId,
                department: formData.department,
                name: formData.name,
                description: formData.description,
                status: formData.status,
            };

            await dispatch(updateSubSessionMaster({ id, formData: payload })).unwrap();

            Swal.fire({
                title: "Success!",
                text: "Sub Session Type updated successfully.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });

            navigate("/sub-session-types");
        } catch (err) {
            Swal.fire({
                title: "Error",
                text: getApiErrorMessage(err, "Failed to update Sub Session Type"),
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    if (isLoading) return <div className="p-3">Loading...</div>;
    if (error) return <div className="p-3 text-danger">{error}</div>;

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        {Object.keys(errors).length > 0 && (
                            <div className="alert alert-danger">
                                {Object.values(errors).map((err, i) => (
                                    <div key={i}>{err}</div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit}>
                            <SubSessionTypeForm
                                formData={formData}
                                handleChange={handleChange}
                            />

                            <div className="row mt-3">
                                <div className="col-md-2 ms-auto">
                                    <button type="submit" className="btn btn-primary w-100 sticky-form-submit">
                                        <i className="ri-edit-2-line me-1"></i> Update
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default EditSubSessionType;
